import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import AppImage from '../../components/AppImage';
import { regionalTeamService } from '../../services/regionalTeamService';
import { regionalTeamRosterService } from '../../services/regionalTeamRosterService';
import { userService } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';
import { isRegionAdmin } from '../../utils/tanzaniaRegions';

const POSITION_OPTIONS = [
  { value: 'point_guard', label: 'Point Guard (PG)' },
  { value: 'shooting_guard', label: 'Shooting Guard (SG)' },
  { value: 'small_forward', label: 'Small Forward (SF)' },
  { value: 'power_forward', label: 'Power Forward (PF)' },
  { value: 'center', label: 'Center (C)' },
];

const OFFICIAL_ROLE_OPTIONS = [
  { value: 'Head Coach', label: 'Head Coach' },
  { value: 'Assistant Coach', label: 'Assistant Coach' },
  { value: 'Team Physician', label: 'Team Physician' },
  { value: 'Team Statistician', label: 'Team Statistician' },
  { value: 'Team manager', label: 'Team manager' },
];

const EMPTY_PLAYER = {
  fullName: '',
  dateOfBirth: '',
  age: '',
  position: '',
  email: '',
  phone: '',
  photoFile: null,
  photoPreview: null,
  birthCertificateFile: null,
};

const EMPTY_OFFICIAL = {
  fullName: '',
  email: '',
  phone: '',
  roleTitle: '',
};

const computeAge = (dateOfBirth) => {
  if (!dateOfBirth) return '';
  const birth = new Date(dateOfBirth);
  if (Number.isNaN(birth.getTime())) return '';
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age >= 0 ? age : '';
};

const getPositionLabel = (value) =>
  POSITION_OPTIONS.find((option) => option.value === value)?.label || value || '—';

const toDateTimeLocalValue = (isoValue) => {
  if (!isoValue) return '';
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const fromDateTimeLocalValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
};

const RegionalTeamPlayersRegistration = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userProfile } = useAuth();
  const regionAdmin = isRegionAdmin(userProfile?.role);
  const assignedRegion = userProfile?.region || '';
  const isGlobalAdmin = userProfile?.role === 'admin';
  const isStaff = userProfile?.role === 'staff';
  const isRestrictedRole = regionAdmin || isStaff;
  const canDeletePlayers = isGlobalAdmin || regionAdmin;
  const photoInputRef = useRef(null);
  const birthCertInputRef = useRef(null);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [view, setView] = useState('list'); // 'list' | 'form'

  const [allPlayers, setAllPlayers] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [listError, setListError] = useState(null);
  const [search, setSearch] = useState('');
  const [teamFilter, setTeamFilter] = useState('all');
  const [photoViewer, setPhotoViewer] = useState(null);
  const [playerPendingDelete, setPlayerPendingDelete] = useState(null);
  const [isDeletingPlayer, setIsDeletingPlayer] = useState(false);

  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [players, setPlayers] = useState([]);
  const [officials, setOfficials] = useState([]);
  const [playerDraft, setPlayerDraft] = useState(EMPTY_PLAYER);
  const [officialDraft, setOfficialDraft] = useState(EMPTY_OFFICIAL);
  const [playerErrors, setPlayerErrors] = useState({});
  const [officialErrors, setOfficialErrors] = useState({});
  const [isLoadingTeams, setIsLoadingTeams] = useState(true);
  const [isLoadingRoster, setIsLoadingRoster] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [registrationWindow, setRegistrationWindow] = useState(null);
  const [registrationAccessLoading, setRegistrationAccessLoading] = useState(false);
  const [canRegisterPlayersNow, setCanRegisterPlayersNow] = useState(isGlobalAdmin);
  const [eligibleGrantUsers, setEligibleGrantUsers] = useState([]);
  const [grantUserId, setGrantUserId] = useState('');
  const [grantUntilLocal, setGrantUntilLocal] = useState('');
  const [isSavingWindow, setIsSavingWindow] = useState(false);
  const [isSavingGrant, setIsSavingGrant] = useState(false);

  const maxPlayers = regionalTeamRosterService.MAX_PLAYERS;
  const maxOfficials = regionalTeamRosterService.MAX_OFFICIALS;
  const registrationLocked = isRestrictedRole && !canRegisterPlayersNow;

  const requiresBirthCertificate = useMemo(() => {
    const age = Number(playerDraft.age);
    return Number.isFinite(age) && age <= 21;
  }, [playerDraft.age]);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/admin-dashboard' },
    { label: 'Regional Team Players Registration' },
  ];

  const loadAllPlayers = useCallback(async () => {
    setIsLoadingList(true);
    setListError(null);
    try {
      const data = await regionalTeamRosterService.getAllPlayers();
      const list = data || [];
      setAllPlayers(
        regionAdmin && assignedRegion
          ? list.filter((player) => player.teamRegion === assignedRegion)
          : list
      );
    } catch (err) {
      setListError(err.message || 'Failed to load regional team players');
    } finally {
      setIsLoadingList(false);
    }
  }, [regionAdmin, assignedRegion]);

  const loadTeams = useCallback(async () => {
    setIsLoadingTeams(true);
    try {
      const data = await regionalTeamService.getAll();
      const list = data || [];
      const scoped =
        regionAdmin && assignedRegion
          ? list.filter((team) => team.region === assignedRegion)
          : list;
      setTeams(scoped);
      return scoped;
    } catch (err) {
      setError(err.message || 'Failed to load regional teams.');
      return [];
    } finally {
      setIsLoadingTeams(false);
    }
  }, [regionAdmin, assignedRegion]);

  const loadRegistrationControls = useCallback(async () => {
    setRegistrationAccessLoading(true);
    try {
      const [windowData, hasAccess] = await Promise.all([
        userService.getRegionalPlayerRegistrationWindow(),
        userService.checkRegionalPlayerRegistrationAccess(),
      ]);
      setRegistrationWindow(windowData);
      setCanRegisterPlayersNow(!!hasAccess || isGlobalAdmin);

      if (isGlobalAdmin) {
        const users = await userService.listUsers({ role: 'all' });
        const scoped = (users || []).filter((u) =>
          ['staff', 'region_admin'].includes(u.role)
        );
        setEligibleGrantUsers(scoped);
      }
    } catch (err) {
      setError(
        err?.message || 'Failed to load regional player registration controls.'
      );
    } finally {
      setRegistrationAccessLoading(false);
    }
  }, [isGlobalAdmin]);

  useEffect(() => {
    loadAllPlayers();
    loadTeams();
    loadRegistrationControls();
  }, [loadAllPlayers, loadTeams, loadRegistrationControls]);

  useEffect(() => {
    const teamId = searchParams.get('teamId');
    if (teamId) {
      setSelectedTeamId(teamId);
      setView('form');
    }
  }, [searchParams]);

  useEffect(() => {
    const loadRoster = async () => {
      if (view !== 'form' || !selectedTeamId) {
        return;
      }
      setIsLoadingRoster(true);
      try {
        const [existingPlayers, existingOfficials] = await Promise.all([
          regionalTeamRosterService.getPlayers(selectedTeamId),
          regionalTeamRosterService.getOfficials(selectedTeamId),
        ]);
        setPlayers(
          (existingPlayers || []).map((p) => ({
            key: p.id,
            fullName: p.fullName || '',
            dateOfBirth: p.dateOfBirth || '',
            age: p.age != null ? String(p.age) : computeAge(p.dateOfBirth),
            position: p.position || '',
            email: p.email || '',
            phone: p.phone || '',
            photoFile: null,
            photoPreview: p.photoUrl || null,
            photoUrl: p.photoUrl || null,
            photoPath: p.photoPath || null,
            birthCertificateFile: null,
            birthCertificateName: p.birthCertificateUrl ? 'On file' : null,
            birthCertificateUrl: p.birthCertificateUrl || null,
            birthCertificatePath: p.birthCertificatePath || null,
          }))
        );
        setOfficials(
          (existingOfficials || []).map((o) => ({
            key: o.id,
            fullName: o.fullName || '',
            email: o.email || '',
            phone: o.phone || '',
            roleTitle: o.roleTitle || '',
          }))
        );
      } catch (err) {
        console.error(err);
        setError(err.message || 'Failed to load existing roster.');
      } finally {
        setIsLoadingRoster(false);
      }
    };
    loadRoster();
  }, [selectedTeamId, view]);

  const teamOptions = teams.map((team) => ({
    value: team.id,
    label: team.teamName,
  }));

  const teamFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All regional teams' },
      ...teams.map((team) => ({ value: team.id, label: team.teamName })),
    ],
    [teams]
  );

  const filteredPlayers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return allPlayers.filter((player) => {
      if (teamFilter !== 'all' && player.teamId !== teamFilter) return false;
      if (!query) return true;
      return [player.fullName, player.teamName, player.position, player.email, player.phone]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [allPlayers, search, teamFilter]);

  const openRegisterForm = (teamId = '') => {
    if (registrationLocked) {
      setError(
        'Registration is currently closed for your account. Contact a system administrator for access.'
      );
      return;
    }
    setSuccess(null);
    setError(null);
    setSelectedTeamId(teamId || '');
    setPlayers([]);
    setOfficials([]);
    setPlayerDraft(EMPTY_PLAYER);
    setOfficialDraft(EMPTY_OFFICIAL);
    setPlayerErrors({});
    setOfficialErrors({});
    setView('form');
    if (teamId) {
      setSearchParams({ teamId });
    } else {
      setSearchParams({});
    }
  };

  const backToList = async () => {
    setView('list');
    setSearchParams({});
    setSelectedTeamId('');
    setPlayers([]);
    setOfficials([]);
    setError(null);
    await loadAllPlayers();
  };

  const handleWindowFieldChange = (field, value) => {
    setRegistrationWindow((prev) => ({
      ...(prev || { windowKey: 'regional_team_players', enabled: false }),
      [field]: value,
    }));
  };

  const saveRegistrationWindow = async () => {
    if (!isGlobalAdmin || !registrationWindow) return;
    setIsSavingWindow(true);
    setError(null);
    setSuccess(null);
    try {
      await userService.updateRegionalPlayerRegistrationWindow({
        enabled: !!registrationWindow.enabled,
        opensAt: registrationWindow.opensAt,
        closesAt: registrationWindow.closesAt,
      });
      setSuccess('Regional team player registration window updated.');
      await loadRegistrationControls();
    } catch (err) {
      setError(err?.message || 'Failed to save registration window.');
    } finally {
      setIsSavingWindow(false);
    }
  };

  const handleGrantAccess = async () => {
    if (!isGlobalAdmin) return;
    if (!grantUserId) {
      setError('Select a user to grant registration access.');
      return;
    }
    const grantUntilIso = fromDateTimeLocalValue(grantUntilLocal);
    if (!grantUntilIso) {
      setError('Choose a valid grant expiry date and time.');
      return;
    }

    setIsSavingGrant(true);
    setError(null);
    setSuccess(null);
    try {
      await userService.setRegionalPlayerRegistrationOverride(
        grantUserId,
        grantUntilIso
      );
      setSuccess('Individual registration access granted.');
      setGrantUserId('');
      setGrantUntilLocal('');
      await loadRegistrationControls();
    } catch (err) {
      setError(err?.message || 'Failed to grant individual access.');
    } finally {
      setIsSavingGrant(false);
    }
  };

  const handleRevokeAccess = async (userId) => {
    if (!isGlobalAdmin || !userId) return;
    setIsSavingGrant(true);
    setError(null);
    setSuccess(null);
    try {
      await userService.setRegionalPlayerRegistrationOverride(userId, null);
      setSuccess('Individual registration access revoked.');
      await loadRegistrationControls();
    } catch (err) {
      setError(err?.message || 'Failed to revoke individual access.');
    } finally {
      setIsSavingGrant(false);
    }
  };

  const canDeletePlayer = (player) => {
    if (!canDeletePlayers || !player) return false;
    if (isGlobalAdmin) return true;
    if (regionAdmin && assignedRegion) {
      if (player.teamRegion) return player.teamRegion === assignedRegion;
      const team = teams.find((t) => t.id === player.teamId);
      return team?.region === assignedRegion;
    }
    return false;
  };

  const handleConfirmDeletePlayer = async () => {
    if (!playerPendingDelete?.id) return;
    setIsDeletingPlayer(true);
    setListError(null);
    const deleted = playerPendingDelete;
    try {
      await regionalTeamRosterService.deletePlayer(deleted.id);
      setSuccess(`Deleted player “${deleted.fullName || 'Unnamed'}”.`);
      setPlayerPendingDelete(null);
      await loadAllPlayers();
      if (selectedTeamId && selectedTeamId === deleted.teamId && view === 'form') {
        setPlayers((prev) => prev.filter((p) => p.id !== deleted.id));
      }
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setListError(err.message || 'Failed to delete regional player');
    } finally {
      setIsDeletingPlayer(false);
    }
  };

  const updatePlayerDraft = (field, value) => {
    setPlayerDraft((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'dateOfBirth') {
        next.age = computeAge(value);
      }
      return next;
    });
    if (playerErrors[field]) {
      setPlayerErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (playerDraft.photoPreview && playerDraft.photoFile) {
      URL.revokeObjectURL(playerDraft.photoPreview);
    }
    setPlayerDraft((prev) => ({
      ...prev,
      photoFile: file,
      photoPreview: file ? URL.createObjectURL(file) : null,
    }));
    if (playerErrors.photoFile) {
      setPlayerErrors((prev) => ({ ...prev, photoFile: undefined }));
    }
  };

  const handleBirthCertificateChange = (event) => {
    const file = event.target.files?.[0] || null;
    setPlayerDraft((prev) => ({ ...prev, birthCertificateFile: file }));
    if (playerErrors.birthCertificateFile) {
      setPlayerErrors((prev) => ({ ...prev, birthCertificateFile: undefined }));
    }
  };

  const resetPlayerDraft = () => {
    if (playerDraft.photoPreview && playerDraft.photoFile) {
      URL.revokeObjectURL(playerDraft.photoPreview);
    }
    setPlayerDraft(EMPTY_PLAYER);
    setPlayerErrors({});
    if (photoInputRef.current) photoInputRef.current.value = '';
    if (birthCertInputRef.current) birthCertInputRef.current.value = '';
  };

  const handleAddPlayer = () => {
    if (registrationLocked) {
      setError(
        'Registration is currently closed for your account. Contact a system administrator for access.'
      );
      return;
    }
    setSuccess(null);
    setError(null);
    const nextErrors = {};

    if (!playerDraft.fullName?.trim()) {
      nextErrors.fullName = 'Player full name is required to add them to the roster';
    }
    if (players.length >= maxPlayers) {
      setError(`Maximum of ${maxPlayers} players allowed.`);
      return;
    }
    if (Object.keys(nextErrors).length > 0) {
      setPlayerErrors(nextErrors);
      return;
    }

    setPlayers((prev) => [
      ...prev,
      {
        key: `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        fullName: playerDraft.fullName.trim(),
        dateOfBirth: playerDraft.dateOfBirth || '',
        age: playerDraft.age !== '' && playerDraft.age != null ? String(playerDraft.age) : '',
        position: playerDraft.position || '',
        email: playerDraft.email?.trim() || '',
        phone: playerDraft.phone?.trim() || '',
        photoFile: playerDraft.photoFile,
        photoPreview: playerDraft.photoPreview,
        birthCertificateFile: playerDraft.birthCertificateFile,
        birthCertificateName: playerDraft.birthCertificateFile?.name || null,
      },
    ]);
    setPlayerDraft(EMPTY_PLAYER);
    setPlayerErrors({});
    if (photoInputRef.current) photoInputRef.current.value = '';
    if (birthCertInputRef.current) birthCertInputRef.current.value = '';
  };

  const handleRemovePlayer = (key) => {
    setPlayers((prev) => {
      const target = prev.find((player) => player.key === key);
      if (target?.photoPreview && target?.photoFile) {
        URL.revokeObjectURL(target.photoPreview);
      }
      return prev.filter((player) => player.key !== key);
    });
  };

  const handleAddOfficial = () => {
    if (registrationLocked) {
      setError(
        'Registration is currently closed for your account. Contact a system administrator for access.'
      );
      return;
    }
    setSuccess(null);
    setError(null);
    const nextErrors = {};
    if (!officialDraft.fullName?.trim()) {
      nextErrors.fullName = 'Official full name is required to add them to the roster';
    }
    if (officials.length >= maxOfficials) {
      setError(`Maximum of ${maxOfficials} other team officials allowed.`);
      return;
    }
    if (Object.keys(nextErrors).length > 0) {
      setOfficialErrors(nextErrors);
      return;
    }

    setOfficials((prev) => [
      ...prev,
      {
        key: `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        fullName: officialDraft.fullName.trim(),
        email: officialDraft.email?.trim() || '',
        phone: officialDraft.phone?.trim() || '',
        roleTitle: officialDraft.roleTitle || '',
      },
    ]);
    setOfficialDraft(EMPTY_OFFICIAL);
    setOfficialErrors({});
  };

  const handleRemoveOfficial = (key) => {
    setOfficials((prev) => prev.filter((official) => official.key !== key));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSuccess(null);
    setError(null);

    if (registrationLocked) {
      setError(
        'Registration is currently closed for your account. Contact a system administrator for access.'
      );
      return;
    }

    if (!selectedTeamId) {
      setError('Please select a regional team.');
      return;
    }
    if (players.length > maxPlayers) {
      setError(`Maximum of ${maxPlayers} players allowed.`);
      return;
    }
    if (officials.length > maxOfficials) {
      setError(`Maximum of ${maxOfficials} other team officials allowed.`);
      return;
    }

    setIsSubmitting(true);
    try {
      await regionalTeamRosterService.saveRoster(selectedTeamId, { players, officials });
      setSuccess('Regional team roster saved successfully.');
      await loadAllPlayers();
      setTimeout(() => {
        setView('list');
        setSearchParams({});
      }, 800);
    } catch (err) {
      setError(err.message || 'Failed to save regional team roster.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <main
        className={`pt-16 transition-all duration-300 ${
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
        }`}
      >
        <div className="p-6">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mt-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="UsersRound" size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">
                    Regional Team Players Registration
                  </h1>
                  <p className="text-muted-foreground">
                    {view === 'list'
                      ? 'Browse registered regional team players by team'
                      : `Register or update roster (max ${maxPlayers} players, ${maxOfficials} officials)`}
                  </p>
                </div>
              </div>

              {view === 'list' ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    iconName="Map"
                    iconPosition="left"
                    onClick={() => navigate('/regional-team-registration')}
                  >
                    Regional Teams
                  </Button>
                  <Button
                    iconName="Plus"
                    iconPosition="left"
                    onClick={() => openRegisterForm()}
                    disabled={registrationLocked}
                  >
                    Register New Player
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  iconName="ArrowLeft"
                  iconPosition="left"
                  onClick={backToList}
                >
                  Back to List
                </Button>
              )}
            </div>
          </div>

          {registrationAccessLoading && (
            <div className="mb-6 px-4 py-3 rounded-lg border bg-muted/40 border-border text-muted-foreground flex items-center gap-2">
              <Icon name="Loader2" size={16} className="animate-spin" />
              Loading registration access controls...
            </div>
          )}

          {!registrationAccessLoading && registrationLocked && (
            <div className="mb-6 px-4 py-3 rounded-lg border bg-warning/10 border-warning/20 text-warning">
              <div className="flex items-start gap-3">
                <Icon name="Lock" size={18} />
                <div>
                  <p className="font-medium">
                    Regional team player registration is closed for your account.
                  </p>
                  <p className="text-sm mt-1">
                    You can view players, but adding or saving new registrations is disabled
                    until a system administrator opens the registration window or grants you
                    individual access.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!registrationAccessLoading && isGlobalAdmin && registrationWindow && (
            <div className="mb-6 bg-card rounded-lg border border-border card-shadow p-6 space-y-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Icon name="ShieldCheck" size={20} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">
                    Registration Control (Admin)
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Define when regional team player registration is globally open for staff and
                    region admins, and grant individual temporary access.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <label className="flex items-center gap-2 text-sm text-foreground border border-border rounded-lg px-3 py-2">
                  <input
                    type="checkbox"
                    checked={!!registrationWindow.enabled}
                    onChange={(e) =>
                      handleWindowFieldChange('enabled', e.target.checked)
                    }
                  />
                  Enable global registration window
                </label>
                <Input
                  type="datetime-local"
                  label="Opens at"
                  value={toDateTimeLocalValue(registrationWindow.opensAt)}
                  onChange={(e) =>
                    handleWindowFieldChange(
                      'opensAt',
                      fromDateTimeLocalValue(e.target.value)
                    )
                  }
                />
                <Input
                  type="datetime-local"
                  label="Closes at"
                  value={toDateTimeLocalValue(registrationWindow.closesAt)}
                  onChange={(e) =>
                    handleWindowFieldChange(
                      'closesAt',
                      fromDateTimeLocalValue(e.target.value)
                    )
                  }
                />
              </div>

              <div className="flex justify-end">
                <Button
                  iconName="Save"
                  iconPosition="left"
                  loading={isSavingWindow}
                  onClick={saveRegistrationWindow}
                >
                  Save Window
                </Button>
              </div>

              <div className="border-t border-border pt-4 space-y-3">
                <h3 className="font-semibold text-foreground">Grant Individual Access</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Select
                    label="User"
                    options={[
                      { value: '', label: 'Select staff/region admin' },
                      ...eligibleGrantUsers.map((user) => ({
                        value: user.id,
                        label: `${user.fullName || user.email} (${user.role.replace(/_/g, ' ')})`,
                      })),
                    ]}
                    value={grantUserId}
                    onChange={setGrantUserId}
                    searchable
                  />
                  <Input
                    type="datetime-local"
                    label="Grant until"
                    value={grantUntilLocal}
                    onChange={(e) => setGrantUntilLocal(e.target.value)}
                  />
                  <div className="flex items-end">
                    <Button
                      className="w-full"
                      iconName="KeyRound"
                      iconPosition="left"
                      loading={isSavingGrant}
                      onClick={handleGrantAccess}
                    >
                      Grant Access
                    </Button>
                  </div>
                </div>

                {eligibleGrantUsers.length > 0 && (
                  <div className="overflow-x-auto border border-border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/40 border-b border-border">
                        <tr>
                          <th className="text-left px-3 py-2">User</th>
                          <th className="text-left px-3 py-2">Role</th>
                          <th className="text-left px-3 py-2">Override Until</th>
                          <th className="text-right px-3 py-2">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eligibleGrantUsers.map((user) => (
                          <tr key={user.id} className="border-b border-border last:border-0">
                            <td className="px-3 py-2 text-foreground">
                              {user.fullName || user.email}
                            </td>
                            <td className="px-3 py-2 text-muted-foreground capitalize">
                              {user.role.replace(/_/g, ' ')}
                            </td>
                            <td className="px-3 py-2 text-foreground">
                              {user.regionalPlayerRegistrationOverrideUntil
                                ? new Date(
                                    user.regionalPlayerRegistrationOverrideUntil
                                  ).toLocaleString()
                                : '—'}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                iconName="ShieldX"
                                onClick={() => handleRevokeAccess(user.id)}
                                disabled={
                                  !user.regionalPlayerRegistrationOverrideUntil ||
                                  isSavingGrant
                                }
                              >
                                Revoke
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {success && (
            <div className="mb-6 px-4 py-3 rounded-lg border bg-success/10 border-success/20 text-success">
              <div className="flex items-start gap-3">
                <Icon name="CheckCircle2" size={18} />
                <p className="font-medium">{success}</p>
              </div>
            </div>
          )}

          {(error || listError) && (
            <div className="mb-6 px-4 py-3 rounded-lg border bg-destructive/10 border-destructive/20 text-destructive">
              <div className="flex items-start gap-3">
                <Icon name="AlertTriangle" size={18} />
                <p className="font-medium">{error || listError}</p>
              </div>
            </div>
          )}

          {view === 'list' ? (
            <div className="space-y-4">
              <div className="bg-card rounded-lg border border-border card-shadow p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                  <div className="md:col-span-1">
                    <Input
                      type="search"
                      placeholder="Search by player, team, position..."
                      iconName="Search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                  <Select
                    label="Regional Team"
                    options={teamFilterOptions}
                    value={teamFilter}
                    onChange={setTeamFilter}
                    placeholder="Filter by team"
                  />
                  <p className="text-sm text-muted-foreground md:text-right pb-2">
                    {filteredPlayers.length} of {allPlayers.length} players
                  </p>
                </div>
              </div>

              {isLoadingList ? (
                <div className="bg-card rounded-lg border border-border card-shadow p-12 text-center">
                  <Icon name="Loader2" size={40} className="text-primary mx-auto mb-4 animate-spin" />
                  <p className="text-muted-foreground">Loading regional team players...</p>
                </div>
              ) : filteredPlayers.length === 0 ? (
                <div className="bg-card rounded-lg border border-border card-shadow p-12 text-center">
                  <Icon name="UsersRound" size={40} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {allPlayers.length === 0 ? 'No Players Registered' : 'No Matching Players'}
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    {allPlayers.length === 0
                      ? 'Register the first regional team player to get started.'
                      : 'Try adjusting your search or team filter.'}
                  </p>
                  {allPlayers.length === 0 && (
                    <Button
                      iconName="Plus"
                      iconPosition="left"
                      onClick={() => openRegisterForm()}
                      disabled={registrationLocked}
                    >
                      Register New Player
                    </Button>
                  )}
                </div>
              ) : (
                <div className="bg-card rounded-lg border border-border card-shadow overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 border-b border-border">
                        <tr className="text-left">
                          <th className="px-4 py-3 font-semibold text-foreground">Player</th>
                          <th className="px-4 py-3 font-semibold text-foreground">Position</th>
                          <th className="px-4 py-3 font-semibold text-foreground">Age</th>
                          <th className="px-4 py-3 font-semibold text-foreground">Regional Team</th>
                          <th className="px-4 py-3 font-semibold text-foreground">Contact</th>
                          <th className="px-4 py-3 font-semibold text-foreground text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPlayers.map((player) => (
                          <tr
                            key={player.id}
                            className="border-b border-border last:border-0 hover:bg-muted/30"
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3 min-w-[180px]">
                                {player.photoUrl ? (
                                  <button
                                    type="button"
                                    className="w-11 h-11 rounded-full overflow-hidden border border-border shrink-0 hover:ring-2 hover:ring-primary"
                                    onClick={() =>
                                      setPhotoViewer({ src: player.photoUrl, alt: player.fullName })
                                    }
                                    title="View photo"
                                  >
                                    <AppImage
                                      src={player.photoUrl}
                                      alt={player.fullName}
                                      className="w-full h-full object-cover"
                                    />
                                  </button>
                                ) : (
                                  <div className="w-11 h-11 rounded-full bg-muted flex items-center justify-center shrink-0">
                                    <Icon name="User" size={18} className="text-muted-foreground" />
                                  </div>
                                )}
                                <p className="font-medium text-foreground truncate">
                                  {player.fullName || 'Unnamed Player'}
                                </p>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-foreground whitespace-nowrap">
                              {getPositionLabel(player.position)}
                            </td>
                            <td className="px-4 py-3 text-foreground whitespace-nowrap">
                              {player.age != null && player.age !== '' ? player.age : '—'}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2 min-w-[160px]">
                                <div className="w-8 h-8 rounded bg-white border border-border overflow-hidden flex items-center justify-center shrink-0">
                                  {player.teamLogoUrl ? (
                                    <AppImage
                                      src={player.teamLogoUrl}
                                      alt={player.teamName}
                                      className="w-full h-full object-contain p-0.5"
                                    />
                                  ) : (
                                    <Icon name="Shield" size={14} className="text-primary" />
                                  )}
                                </div>
                                <span className="text-foreground truncate">{player.teamName}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="min-w-[120px]">
                                <p className="text-foreground truncate text-xs">
                                  {player.email || '—'}
                                </p>
                                <p className="text-muted-foreground text-xs">{player.phone || '—'}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  iconName="Pencil"
                                  iconPosition="left"
                                  onClick={() => openRegisterForm(player.teamId)}
                                  disabled={registrationLocked}
                                >
                                  Manage Roster
                                </Button>
                                {canDeletePlayer(player) && (
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    iconName="Trash2"
                                    onClick={() => setPlayerPendingDelete(player)}
                                  >
                                    Delete
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6 max-w-5xl">
              <div className="bg-card rounded-lg border border-border card-shadow p-6">
                <Select
                  label="Regional Team"
                  required
                  options={teamOptions}
                  value={selectedTeamId}
                  onChange={(value) => {
                    setSelectedTeamId(value);
                    if (value) setSearchParams({ teamId: value });
                  }}
                  placeholder={isLoadingTeams ? 'Loading teams...' : 'Select regional team'}
                  disabled={isLoadingTeams || teamOptions.length === 0}
                  description={
                    teamOptions.length === 0 && !isLoadingTeams
                      ? 'Register a regional team first before adding players.'
                      : isLoadingRoster
                        ? 'Loading current roster...'
                        : undefined
                  }
                />
              </div>

              <div className="bg-card rounded-lg border border-border card-shadow overflow-hidden">
                <div className="border-b border-border px-6 py-4 flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Players</h2>
                    <p className="text-sm text-muted-foreground">
                      {players.length} / {maxPlayers} players added
                    </p>
                  </div>
                  <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                    Max {maxPlayers}
                  </span>
                </div>

                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="rounded-lg border border-border p-3">
                      <p className="text-sm font-medium text-foreground mb-2">Player Photo</p>
                      <div className="aspect-square rounded-md border border-dashed border-border bg-muted/30 flex items-center justify-center overflow-hidden mb-3">
                        {playerDraft.photoPreview ? (
                          <AppImage
                            src={playerDraft.photoPreview}
                            alt="Player photo preview"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Icon name="User" size={32} className="text-muted-foreground" />
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        iconName="Upload"
                        iconPosition="left"
                        onClick={() => photoInputRef.current?.click()}
                        className="w-full"
                      >
                        Choose Photo
                      </Button>
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handlePhotoChange}
                      />
                    </div>

                    <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input
                        label="Player Full Name"
                        required
                        value={playerDraft.fullName}
                        onChange={(e) => updatePlayerDraft('fullName', e.target.value)}
                        error={playerErrors.fullName}
                        placeholder="Full name"
                      />
                      <Select
                        label="Position / Role"
                        options={POSITION_OPTIONS}
                        value={playerDraft.position}
                        onChange={(value) => updatePlayerDraft('position', value)}
                        placeholder="Select position (optional)"
                      />
                      <Input
                        label="Date of Birth"
                        type="date"
                        value={playerDraft.dateOfBirth}
                        onChange={(e) => updatePlayerDraft('dateOfBirth', e.target.value)}
                      />
                      <Input
                        label="Age"
                        value={playerDraft.age === '' ? '' : String(playerDraft.age)}
                        readOnly
                        description="Auto-computed from date of birth"
                      />
                      <Input
                        label="Email"
                        type="email"
                        value={playerDraft.email}
                        onChange={(e) => updatePlayerDraft('email', e.target.value)}
                        placeholder="player@example.com (optional)"
                      />
                      <Input
                        label="Phone"
                        value={playerDraft.phone}
                        onChange={(e) => updatePlayerDraft('phone', e.target.value)}
                        placeholder="07XXXXXXXX (optional)"
                      />
                    </div>
                  </div>

                  {requiresBirthCertificate && (
                    <div className="rounded-lg border border-border bg-muted/30 p-4">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            Birth Certificate (recommended for age 21 and below)
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {playerDraft.birthCertificateFile?.name || 'Optional — no file chosen'}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          iconName="Upload"
                          iconPosition="left"
                          onClick={() => birthCertInputRef.current?.click()}
                        >
                          Attach Birth Certificate
                        </Button>
                        <input
                          ref={birthCertInputRef}
                          type="file"
                          accept=".pdf,.doc,.docx,image/*"
                          className="hidden"
                          onChange={handleBirthCertificateChange}
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      iconName="UserPlus"
                      iconPosition="left"
                      onClick={handleAddPlayer}
                      disabled={
                        registrationLocked || !selectedTeamId || players.length >= maxPlayers
                      }
                    >
                      Add Player
                    </Button>
                    <Button type="button" variant="ghost" onClick={resetPlayerDraft}>
                      Clear Form
                    </Button>
                  </div>

                  {players.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4">
                      No players on this roster yet. Add players above, then save.
                    </p>
                  ) : (
                    <div className="border border-border rounded-lg overflow-x-auto">
                      <table className="w-full text-sm min-w-[720px]">
                        <thead className="bg-muted/40">
                          <tr>
                            <th className="text-left px-4 py-2 font-medium text-muted-foreground">Photo</th>
                            <th className="text-left px-4 py-2 font-medium text-muted-foreground">Name</th>
                            <th className="text-left px-4 py-2 font-medium text-muted-foreground">Age</th>
                            <th className="text-left px-4 py-2 font-medium text-muted-foreground">Position</th>
                            <th className="text-left px-4 py-2 font-medium text-muted-foreground">Contacts</th>
                            <th className="text-right px-4 py-2 font-medium text-muted-foreground">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {players.map((player) => (
                            <tr key={player.key} className="border-t border-border">
                              <td className="px-4 py-2">
                                <div className="w-10 h-10 rounded-full overflow-hidden bg-muted flex items-center justify-center">
                                  {player.photoPreview ? (
                                    <AppImage
                                      src={player.photoPreview}
                                      alt={player.fullName}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <Icon name="User" size={16} className="text-muted-foreground" />
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-2 text-foreground font-medium">{player.fullName}</td>
                              <td className="px-4 py-2 text-foreground">{player.age || '—'}</td>
                              <td className="px-4 py-2 text-foreground">
                                {getPositionLabel(player.position)}
                              </td>
                              <td className="px-4 py-2 text-foreground">
                                <div className="text-xs">
                                  <div>{player.email || '—'}</div>
                                  <div className="text-muted-foreground">{player.phone || '—'}</div>
                                </div>
                              </td>
                              <td className="px-4 py-2 text-right">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  iconName="Trash2"
                                  onClick={() => handleRemovePlayer(player.key)}
                                >
                                  Remove
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-card rounded-lg border border-border card-shadow overflow-hidden">
                <div className="border-b border-border px-6 py-4 flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Other Team Officials</h2>
                    <p className="text-sm text-muted-foreground">
                      {officials.length} / {maxOfficials} officials added
                    </p>
                  </div>
                  <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                    Max {maxOfficials}
                  </span>
                </div>

                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input
                      label="Official Full Name"
                      required
                      value={officialDraft.fullName}
                      onChange={(e) => {
                        setOfficialDraft((prev) => ({ ...prev, fullName: e.target.value }));
                        if (officialErrors.fullName) {
                          setOfficialErrors((prev) => ({ ...prev, fullName: undefined }));
                        }
                      }}
                      error={officialErrors.fullName}
                      placeholder="Full name"
                    />
                    <Select
                      label="Official Role"
                      options={OFFICIAL_ROLE_OPTIONS}
                      value={officialDraft.roleTitle}
                      onChange={(value) => {
                        setOfficialDraft((prev) => ({ ...prev, roleTitle: value }));
                      }}
                      placeholder="Select role (optional)"
                    />
                    <Input
                      label="Email"
                      type="email"
                      value={officialDraft.email}
                      onChange={(e) =>
                        setOfficialDraft((prev) => ({ ...prev, email: e.target.value }))
                      }
                      placeholder="official@example.com (optional)"
                    />
                    <Input
                      label="Phone"
                      value={officialDraft.phone}
                      onChange={(e) =>
                        setOfficialDraft((prev) => ({ ...prev, phone: e.target.value }))
                      }
                      placeholder="07XXXXXXXX (optional)"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    iconName="UserPlus"
                    iconPosition="left"
                    onClick={handleAddOfficial}
                    disabled={
                      registrationLocked ||
                      !selectedTeamId ||
                      officials.length >= maxOfficials
                    }
                  >
                    Add Official
                  </Button>

                  {officials.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4">
                      No officials added yet.
                    </p>
                  ) : (
                    <div className="border border-border rounded-lg overflow-x-auto">
                      <table className="w-full text-sm min-w-[640px]">
                        <thead className="bg-muted/40">
                          <tr>
                            <th className="text-left px-4 py-2 font-medium text-muted-foreground">#</th>
                            <th className="text-left px-4 py-2 font-medium text-muted-foreground">Name</th>
                            <th className="text-left px-4 py-2 font-medium text-muted-foreground">Role</th>
                            <th className="text-left px-4 py-2 font-medium text-muted-foreground">Contacts</th>
                            <th className="text-right px-4 py-2 font-medium text-muted-foreground">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {officials.map((official, index) => (
                            <tr key={official.key} className="border-t border-border">
                              <td className="px-4 py-2 text-muted-foreground">{index + 1}</td>
                              <td className="px-4 py-2 text-foreground font-medium">
                                {official.fullName}
                              </td>
                              <td className="px-4 py-2 text-foreground">
                                {official.roleTitle || '—'}
                              </td>
                              <td className="px-4 py-2 text-foreground">
                                <div className="text-xs">
                                  <div>{official.email || '—'}</div>
                                  <div className="text-muted-foreground">{official.phone || '—'}</div>
                                </div>
                              </td>
                              <td className="px-4 py-2 text-right">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  iconName="Trash2"
                                  onClick={() => handleRemoveOfficial(official.key)}
                                >
                                  Remove
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={backToList}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={isSubmitting}
                  iconName="Save"
                  iconPosition="left"
                  disabled={registrationLocked || !selectedTeamId}
                >
                  Save Roster
                </Button>
              </div>
            </form>
          )}
        </div>
      </main>

      {playerPendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !isDeletingPlayer && setPlayerPendingDelete(null)}
          />
          <div className="relative bg-card border border-border rounded-lg card-shadow w-full max-w-md p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                <Icon name="Trash2" size={20} className="text-destructive" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Delete regional player?</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  This permanently deletes{' '}
                  <span className="font-medium text-foreground">
                    {playerPendingDelete.fullName || 'this player'}
                  </span>
                  {playerPendingDelete.teamName ? (
                    <>
                      {' '}
                      from <span className="font-medium text-foreground">{playerPendingDelete.teamName}</span>
                    </>
                  ) : null}
                  . Related tournament stats will also be removed. This cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setPlayerPendingDelete(null)}
                disabled={isDeletingPlayer}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                iconName="Trash2"
                iconPosition="left"
                loading={isDeletingPlayer}
                onClick={handleConfirmDeletePlayer}
              >
                Delete Player
              </Button>
            </div>
          </div>
        </div>
      )}

      {photoViewer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setPhotoViewer(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="relative bg-card rounded-lg border border-border max-w-3xl w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <p className="font-medium text-foreground truncate pr-4">{photoViewer.alt}</p>
              <Button variant="ghost" size="sm" iconName="X" onClick={() => setPhotoViewer(null)} />
            </div>
            <div className="bg-muted/40 flex items-center justify-center p-4">
              <AppImage
                src={photoViewer.src}
                alt={photoViewer.alt}
                className="max-h-[70vh] max-w-full object-contain rounded"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegionalTeamPlayersRegistration;
